<template>
	<div class="footer">
		<div class="row">
			<div class="col-4">
				<img
					@click="navigateTo('Home')"
					src="https://catsitterindonesia.com/image/logo.7e9d97f2.png"
					alt="Catsitter Indonesia logo"
					role="button"
				/>
				<p class="tagline mb-0">Connecting Sitters with Pawrents</p>

				<p class="title mb-0">HEAD OFFICE</p>
				<p
					@click="openLink('Address')"
					class="text mb-0 address"
					role="button"
					aria-label="Open head office address in maps"
				>
					One Pacific Place Lt. 15, Jl. Jend. Sudirman Kav. 52-53, RT.5/RW.3,
					Kecamatan Kebayoran Baru, Jakarta Selatan, Indonesia 12190
				</p>

				<p class="title mb-0">CUSTOMER CARE</p>
				<p
					v-if="hasLinks"
					@click="openLink('Email')"
					class="text contact mb-0"
					role="button"
					aria-label="Send email"
				>
					<SvgIcon
						type="mdi"
						:path="icons.email"
					/>
					{{ emailLabel }}
				</p>
				<p
					v-if="hasLinks"
					@click="openLink('Phone')"
					class="text contact mb-0"
					role="button"
					aria-label="Call phone"
				>
					<SvgIcon
						type="mdi"
						:path="icons.phone"
					/>
					{{ phoneLabel }}
				</p>
			</div>
			<div class="col-8 text-end">
				<ul>
					<li
						role="button"
						@click="navigateTo('Services')"
					>
						Services
					</li>
					<li
						role="button"
						@click="navigateTo('Profile')"
					>
						Cat Sitter Profile
					</li>
					<li
						role="button"
						@click="openLink('Blog')"
					>
						Blog
					</li>
					<li
						role="button"
						@click="openLink('Career')"
					>
						Career
					</li>
					<b-button
						@click="openLink('Contact')"
						class="btn-contact"
						aria-label="Contact us via WhatsApp"
					>
						Contact Us
					</b-button>
				</ul>
			</div>
		</div>

		<hr />

		<div class="row">
			<div class="col-12 col-lg-9">
				<p class="copyright mb-0">
					Copyright © 2024 PT. Gato Integrated Solution
				</p>
			</div>
			<div class="col-12 col-lg-3 text-center text-lg-end">
				<SvgIcon
					@click="openLink('Instagram')"
					class="social-media ig"
					type="mdi"
					:path="icons.instagram"
					aria-label="Open Instagram"
				/>
				<SvgIcon
					@click="openLink('Linkedin')"
					class="social-media li"
					type="mdi"
					:path="icons.linkedin"
					aria-label="Open LinkedIn"
				/>
				<svg
					@click="openLink('Tiktok')"
					class="social-media tt"
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 448 512"
					role="img"
					aria-label="Open TikTok"
				>
					<path
						d="M448 209.9a210.1 210.1 0 0 1 -122.8-39.3V349.4A162.6 162.6 0 1 1 185 188.3V278.2a74.6 74.6 0 1 0 52.2 71.2V0l88 0a121.2 121.2 0 0 0 1.9 22.2h0A122.2 122.2 0 0 0 381 102.4a121.4 121.4 0 0 0 67 20.1z"
						fill="#fff"
					/>
				</svg>
				<SvgIcon
					@click="openLink('Contact')"
					class="social-media wa"
					type="mdi"
					:path="icons.whatsapp"
					aria-label="Open WhatsApp"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import SvgIcon from "@jamescoyle/vue-icon";
import {
	mdiEmail,
	mdiPhone,
	mdiInstagram,
	mdiLinkedin,
	mdiWhatsapp,
} from "@mdi/js";

const BLOG_URL = "https://blog.catsitterindonesia.com/";
const ADDRESS_URL = "https://maps.app.goo.gl/6iZmmBFDf9AuXShc8";
const ICONS = Object.freeze({
	email: mdiEmail,
	phone: mdiPhone,
	instagram: mdiInstagram,
	linkedin: mdiLinkedin,
	whatsapp: mdiWhatsapp,
});

const LINK_RESOLVERS = Object.freeze({
	Email: (vm) => vm.getLinkByType("email")?.url,
	Phone: (vm) => vm.getLinkByType("telephone")?.url,
	Instagram: (vm) => vm.getLinkByType("instagram")?.url,
	Linkedin: (vm) => vm.getLinkByType("linkedin")?.url,
	Career: (vm) => vm.getLinkByType("career")?.url,
	Contact: (vm) => vm.getLinkByType("whatsapp")?.url,
	Tiktok: (vm) => vm.getLinkByType("tiktok")?.url,
	Blog: () => BLOG_URL,
	Address: () => ADDRESS_URL,
});

export default {
	name: "FooterComponent",
	components: { SvgIcon },
	emits: ["trigger-services"],
	props: {
		links: {
			type: Array,
			default: () => [],
			validator: (arr) =>
				Array.isArray(arr) &&
				arr.every(
					(it) =>
						it &&
						typeof it === "object" &&
						typeof it.type === "string" &&
						typeof it.url === "string"
				),
		},
	},
	computed: {
		icons() {
			return ICONS;
		},
		hasLinks() {
			return Boolean(this.links && this.links.length);
		},
		emailLabel() {
			return this.stripValue(this.getLinkByType("email")?.url, /^mailto:/);
		},
		phoneLabel() {
			return this.stripValue(this.getLinkByType("telephone")?.url, /^tel:/);
		},
	},
	methods: {
		getLinkByType(name) {
			return this.links.find((item) => item.name === name);
		},
		stripValue(value, regex) {
			return value ? value.replace(regex, "") : "";
		},
		openSafe(url) {
			if (url) {
				window.open(url, "_blank", "noopener,noreferrer");
			}
		},
		openLink(source) {
			const resolver = LINK_RESOLVERS[source];
			if (!resolver) return;
			const url = resolver(this);
			this.openSafe(url);
		},
		async navigateTo(destination) {
			switch (destination) {
				case "Home":
					return this.$router.push({ name: "Home" });
				case "Profile":
					return this.$router.push({ name: "Profile" });
				case "Services":
					if (this.$route.name !== "Home") {
						await this.$router.push({ name: "Home" });
					}
					return this.$emit("trigger-services");
				default:
					return;
			}
		},
	},
};
</script>

<style scoped>
.footer {
	margin-top: 5.21vw;
	background: #ff5a2a;
	padding: 3.13vw 10vw;
}

.footer img {
	cursor: pointer;
	max-width: 17vw;
}

.footer .tagline {
	color: #fff;
	font-size: 1.25vw;
	font-weight: 600;
	margin-top: 0.52vw;
}

.footer .title {
	color: #fff;
	font-size: 0.83vw;
	font-weight: 600;
	margin-top: 1.56vw;
}

.footer .address:hover {
	cursor: pointer;
}

.footer .text {
	color: #fff;
	font-size: 0.83vw;
	margin-top: 0.26vw;
}

.footer .contact {
	cursor: pointer;
}

.footer .contact:hover {
	color: #fdb23f;
}

.footer ul {
	list-style-type: none;
	padding: 0;
}

.footer ul li {
	display: inline;
	color: #fff;
	font-size: 1.25vw;
	cursor: pointer;
	margin-right: 2.6vw;
}

.footer ul li:hover {
	color: #fdb23f;
}

.footer .btn-contact {
	padding: 0.63vw 1.25vw;
	background: #fff;
	color: #ff5a2a;
	font-weight: 600;
	font-size: 1.04vw;
	border: 0;
	border-radius: 2.5vw;
}

.footer .btn-contact:hover,
.footer .btn-contact:active {
	background: linear-gradient(90deg, #fdb23f, #ff5a2a);
	color: #fff;
}

.footer hr {
	border: 0;
	border-top: 1px solid #fff;
	margin: 1.04vw 0;
}

.footer .copyright {
	color: #fff;
	font-size: 1.25vw;
}

.footer .social-media {
	color: #fff;
	font-size: 1.25vw;
	margin-left: 0.63vw;
	height: 1.25vw;
}

.footer .social-media:hover {
	cursor: pointer;
}

.footer .ig:hover {
	color: #833ab4;
}

.footer .li:hover {
	color: #0b65c2;
}

.footer .tt:hover {
	color: #000 !important;
}

.footer .wa:hover {
	color: #25d366;
}

/* mobile */
@media (max-width: 991.98px) {
	.footer {
		padding: 5vw;
	}

	.footer .col-4,
	.footer .col-8 {
		width: 100%;
	}

	.footer .col-8 {
		text-align: left !important;
		margin-top: 10vw;
	}

	.footer img {
		width: auto;
		max-width: 50vw;
	}

	.footer .tagline {
		color: #fff;
		font-size: 4vw;
		margin-top: 2vw;
	}

	.footer .title {
		font-size: 4vw;
		margin-top: 6vw;
	}

	.footer .text {
		font-size: 4vw;
		margin-top: 1vw;
	}

	.footer ul li {
		font-size: 4vw;
		margin-right: 2.6vw;
	}

	.footer ul li:hover {
		color: #fdb23f;
	}

	.footer .btn-contact {
		padding: 2vw 4vw;
		font-size: 3vw;
		border-radius: 10vw;
	}

	.footer hr {
		margin: 4vw 0;
	}

	.footer .copyright {
		color: #fff;
		font-size: 4vw;
	}

	.footer .social-media {
		font-size: 4vw;
		margin-left: 2vw;
		height: 4vw;
	}
}
</style>
